import React, { Component } from 'react';
import {Link} from 'react-router-dom';

import HttpRunner from '../query';
import EntityIdQuery from '../entityIdQuery';

import cosineDefinition from '../../../styles/images/cs_5.png';
import cosineIntersection from '../../../styles/images/cs_1.png';
import cosineDotProduct from '../../../styles/images/cs_2.png';
import cosineProductMagnitudes from '../../../styles/images/cs_4.png';
import cosineSimilarity from '../../../styles/images/cs_3.png';

import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocV2Enrich extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENRICH)
        })
    }

    render() {
        return (
            <div className="bucket" id="top">
                <span>
                    <span className='heading-1'>Document Enrich</span>
                </span>
                <code className="api-definition">
                    <span className="post-badge">POST</span>&nbsp;{urlHandler.API_ENDPOINT_V2_ENRICH}
                </code>
                <div className="description">The enrich endpoint accepts textual input, then performs the selected text processing and enrichment services, a JSON formatted report is returned.</div>

                <h4 className="heading-2">Example</h4>
                <div className="example-content host">
                    <pre>
                        POST {urlHandler.API_ENDPOINT_V2_ENRICH}<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                        Content-Type: application/json<br/><br/>
                    </pre>
                    <pre>{JSON.stringify(examples.EXAMPLE_REQUEST_V2_ENRICH, null, 3)}</pre>
                </div>
                <HttpRunner example="enrich1" query="enrich" response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENRICH) : null}/>

                <h4 className="heading-2" id="enrichv2_request">Request</h4>
                <div className="description enrich">By default, the endpoint extracts and disambiguates entities for a provided document. Request parameters can specify the requested services included in the output.</div>

                <h4 className="heading-3" id="enrichv2_request_object">Request JSON Object</h4>
                <div className="note">
                    <span className="fa fa-info-circle fa-xl">&nbsp;</span>
                    NOTE: Required fields in the Request JSON Object are denoted with an asterisk (*)
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Values </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">disambiguate</td>
                        <td className="meaning">Indicates if the PERSON, ORGANIZATION, or THING entity mentions should be disambiguated to a Finch Entity Knowledgebase entry.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">disambiguateFields</td>
                        <td className="meaning">
                            List of entity fields (JSON Paths as Strings) that are available from the 
                            Finch Entity Knowledgebases to be returned in the <a href="#enrichv2_disambiguation_object">Disambiguation results</a>.
                        </td>
                        <td className="type">String Array</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">documentId</td>
                        <td className="meaning">A client supplied tracking identifier. This value is included in the response when available.</td>
                        <td className="type">String</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr> 
                        <td className="attribute">documentSentiment</td>
                        <td className="meaning">
                            Indicates if the response should include sentence-level and document-level sentiment.<br/><br/>
                            If true, the <i>sentimentModel</i> parameter must be specified.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">documentSummarization</td>
                        <td className="meaning">Indicates if the enriched document summarization is returned with response.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">documentSummarizationMaxSentences</td>
                        <td className="meaning">
                            Indicates the max number of summary sentences that get returned in the 
                            response. This argument is only used if <i>documentSummarization</i> is true in the request.<br/><br/>
                            <i>documentSummarizationMaxSentences</i> is a suggestion to the model but could return less summary sentences.
                        </td>
                        <td className="type">Integer</td>
                        <td className="constraints">5</td>
                    </tr>
                    <tr>
                        <td className="attribute">summaryModel</td>
                        <td className="meaning">
                            The summary model chosen for summarization enrichment.<br/><br/>
                            Required if <i>documentSummarization</i> or <i>entitySummarization</i> is true in the request.<br/><br/>
                            See <a href="#enrichv2_summarize_models">Summary Models</a>
                        </td>
                        <td className="type">
                            String
                        </td>
                        <td className="constraints">News</td>
                    </tr>
                    <tr>
                        <td className="attribute">entities</td>
                        <td className="meaning">
                            List of entity types to be returned.<br/><br/>
                            If "entities":["*"] is specified, the service will extract all permitted 
                            entity types assigned to the API key.<br/><br/>
                            If "entities":[] is specified, the entities JSON Object Array will be omitted from the 
                            response.<br/><br/>
                            Entity types can be specified at the sub-type level. If the sub-type is 
                            omitted, a base entity type alone will implicitly include all associated 
                            sub-types.<br/><br/>
                            See <a href="#enrichv2_entity_types">Entity Types</a>
                        </td>
                        <td className="type">String Array</td>
                        <td className="constraints">[]</td>
                    </tr>
                    <tr>
                        <td className="attribute">entitySentiment</td>
                        <td className="meaning">
                            Indicates if the response should include mention-level and entity-level sentiment.<br/><br/>
                            If true, the <a href="#enrichv2_sentiment_models"><i>sentimentModel</i></a> parameter must be specified.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">entitySummarization</td>
                        <td className="meaning">Indicates if entity-level summarization is returned with response.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">keyPhrases</td>
                        <td className="meaning">
                            Indicates if the response should include the article's extracted key phrases.<br/><br/>
                            Only available for "en" languageCode.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">languageCode</td>
                        <td className="meaning">
                            Language of input <i>text</i> or <i>convertedDoc</i> to be processed.<br/><br/>
                            Specified as an ISO 639-1 code.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">{'"en"'}</td>
                    </tr>
                    <tr>
                        <td className="attribute">mentions</td>
                        <td className="meaning">
                            Indicates if the response should include an itemized list of the mention document locations for each entity extracted.<br/><br/>
                            If <i>mentions</i> is false any mention-level enrichments are omitted. ie) mention sentiment, mention salience, etc, are omitted
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">sentimentModel</td>
                        <td className="meaning">
                            One of the <a href="#enrichv2_sentiment_models">Sentiment Models</a> chosen for sentiment analysis.<br/><br/>
                            Required if <i>documentSentiment</i> or <i>entitySentiment</i> is true.<br/><br/>
                            See <a href="#enrichv2_sentiment_models">Sentiment Models</a>
                        </td>
                        <td className="type">
                            String
                        </td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">returnText</td>
                        <td className="meaning">Indicates if the response should include the UTF-8 document that was enriched in the translated <i>targetLanguage</i>. This value is equal to the <i>translatedText</i> value in the response when <i>languageCode</i> differs from <i>targetLanguage</i>.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">returnTranslatedText</td>
                        <td className="meaning">Indicates if the response should include the translated document (available for some select non-english <i>languageCode</i>). This value is equal to the <i>text</i> value in the response when <i>languageCode</i> differs from <i>targetLanguage</i>.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">targetLanguage</td>
                        <td className="meaning">The non-native language in which the enrichment and extraction are performed. This value can only be set to the corresponding languageCode value or "en" (english). Specified as an ISO 639-1 code.</td>
                        <td className="type">String</td>
                        <td className="constraints">Defaults to <i>languageCode</i>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">text *</td>
                        <td className="meaning">
                            The UTF-8 encoded string content to be processed.<br/><br/>
                            Required if <i>convertedDoc</i> is not used.
                        </td>

                            {/* Lisa review financial topics */}
                            {/* check if the captialization is consistent or if it is typo that relationship types are inconsistent*/}
                            {/* get updated list of relationship types from lisa */}
                                                        
                            {/* Update to hide GDM modules setting from user settings, double check that it does not affect request*/}
                            
                            {/* documentation custom regex */}

                            
                        <td className="type">String</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">convertedDoc</td>
                        <td className="meaning">
                            The JSON Object from the <Link to="/documentation/doc_v2convertdoc">/api/v2/convert-doc</Link> endpoint.<br/><br/>
                            Required if <i>text</i> is not used, if both <i>text</i> and <i>convertedDoc</i> are passed then the <i>convertedDoc</i> will be used and <i>text</i> ignored.
                        </td>
                        <td className="type"><Link to="/documentation/doc_v2convertdoc#convertdocv2_response">Converted Doc JSON Object</Link></td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">informationExtraction</td>
                        <td className="meaning">
                            If <i>informationExtraction</i> is true, prompt-based information will be extracted. This can be done on input from the <i>text</i> or <i>convertedDoc</i> parameters.<br/><br/>
                            Only available for <i>en languageCode</i>.
                            {/* is this languageCode or targetLanguage */}
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr> 
                     <tr>
                        <td className="attribute">informationExtractionPrompt</td>
                        <td className="meaning">Overrides the default prompt template for information extraction. Should only be set in very rare cases.</td>
                        <td className="type">String</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">topicModel</td>
                        <td className="meaning">
                            The <a href="#enrichv2_topic_models">topic model</a> chosen for topic vector enrichment.<br/><br/>
                            Required if <i>topicVector</i> is true.<br/><br/>
                            See <a href="#enrichv2_topic_models">Topic Models</a>
                        </td>
                        <td className="type">
                            String
                        </td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">topicVector</td>
                        <td className="meaning">
                            Indicates if the response should include a topic vector.<br/><br/>
                            If true, the <a href="#enrichv2_topic_models">topic model</a> parameter must be specified.
                        </td>
                        <td className="type">Boolean </td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">textCorrections</td>
                        <td className="meaning">
                            List of supported input string corrections. Finch For Text can attempt to fix common 
                            issues including capitalization, punctuation and tokenization.<br/><br/>
                            This is particularly helpful when sourcing input from social media, chat, automatic speech recognition or 
                            closed captions where the input may be all lowercase or all uppercase.<br/><br/>
                            NOTE: This parameter will alter the processing of input and subsequently the document enriched. Positions in the output are 
                            relative to the document enriched. Set the <i>returnText</i> parameter to true to have the 
                            document enriched included in the output.<br/><br/>
                            See <a href="#enrichv2_text_corrections">Text Corrections</a>
                        </td>
                        <td className="type">
                            String Array
                        </td>
                        <td className="constraints"></td>
                    </tr>
                    {/* <tr>
                        <td className="attribute">geoModules</td>
                        <td className="meaning">An array of one or more GDMs to be used for place extraction and disambiguation.
                        </td>
                        <td className="type">Array of strings<br/><br/>
                            See <a href="#enrichv2_gdm_modules">GDM modules</a>.
                        </td>
                        <td className="constraints">["base"]</td>
                    </tr> */}
                    <tr>
                        <td className="attribute">publishDate</td>
                        <td className="meaning">
                            <i>publishDate</i> should be the date that the document was written/published. 
                            If <i>publishDate</i> is provided, many relative date mentions can be extracted and resolved to 
                            specific date values, i.e. "yesterday", "next Thursday", "last March".<br/><br/>
                            The supported format is ISO 8601 date and time format (YYYY-MM-DDThh:mm:ssZ). If the time is not 
                            supplied, then T00:00:01Z is assumed.
                        </td>
                        <td className="type">String (ISO 8601 date or date and time)</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                            {/* Document context object and usage
                            {
                                text:"Michael scored a basket"
                                context:{text:"Michael Jordan was a legendary basketball player"}
                            }
                            */}
                        <td className="attribute">context</td>
                        <td className="meaning">
                            {/* clarify that this means no convert-doc or just that text is the only field in the context object */}
                            Gives additional information for the document and/or the document's domain, useful for short documents or snippets from a larger document.<br/><br/>
                            Currently, only <i>text</i> is supported with the use of <i>context</i> JSON Object. The <i>text</i> field of the <i>context</i> object is only used to improve entity disambiguation and enrichment in the document.<br/><br/>
                            No extraction results from <i>text</i> field of the <i>context</i> object will be returned.
                            
                        </td>
                        <td className="type">JSON Object</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">salience</td>
                        <td className="meaning">
                            If enabled, adds an aggregated salience score at the entity-level. Also adds industry sector information at document-level.<br/><br/>
                            NOTE: Entity-level means that salience can be found in the <a href="#enrichv2_entity_object">Entity JSON Object</a> salience is not provided at the mention-level.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">salienceModel</td>
                        <td className="meaning">
                            The <a href="#enrichv2_salience_models">
                                Salience Model</a> used to enrich the document and producing salience scores.<br/><br/>
                                Required if <i>salience</i> is true.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">sentenceSalience</td>
                        <td className="meaning">If enabled, salient sentences are returned at the document-level of the response.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">relationships</td>
                        <td className="meaning">
                            Indicates if entity relationship extraction should be performed. <br/><br/>
                            Only available for <i>en</i> languageCode.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">tags</td>
                        <td className="meaning">
                            Tags from various classifiers. If enabled, the tags from the tag service are returned in the response.<br/><br/>
                            Only available for <i>en</i> languageCode.<br/><br/>
                        </td>
                        <td className="type">
                            Boolean
                        </td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">tagModels</td>
                        <td className="meaning">
                            If <i>tags</i> is true, the tag service will attempt to use the requested models.<br/><br/>
                            See <a href="#enrichv2_tag_models">Tag Models</a>
                        </td>
                        <td className="type">
                            String Array
                        </td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">tagThresholdNegative</td>
                        <td className="meaning">If <i>tags</i> is true, the tag service will only return tags within the negative threshold in the response.</td>
                        <td className="type">Decimal</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">tagThresholdPositive</td>
                        <td className="meaning">If <i>tags</i> is true, the tag service will only return tags within the positive threshold in the response.</td>
                        <td className="type">Decimal</td>
                        <td className="constraints"></td>
                    </tr>
                    <tr>
                        <td className="attribute">coreferences</td>
                        <td className="meaning">
                            If <i>coreferences</i> is true, pronouns (he, she, etc) that are coreferences to other entities will be extracted and disambiguated.<br/><br/>
                            Only available for <i>en</i> languageCode.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    <tr>
                        <td className="attribute">geocodeStreets</td>
                        <td className="meaning">Attempts to geocode extracted street addresses by enriching the corresponding entities with latitude, longitude and other GEO features.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">false</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_entity_types">Entity Types</h4>
                <ul className="bullet-list">
                    <li><Link to="/documentation/doc_acronym#top">ACRONYM</Link></li>
                    <li><Link to="/documentation/doc_datetime#top">DATETIME</Link></li>
                    <li><Link to="/documentation/doc_datetimerange#top">DATETIME_RANGE</Link></li>
                    <li><Link to="/documentation/doc_email#top">EMAIL</Link></li>
                    <li><Link to="/documentation/doc_geo#top">GEO</Link></li>
                    <li><Link to="/documentation/doc_ipv4#top">IPV4</Link></li>
                    <li><Link to="/documentation/doc_cyber#top">CYBER/CVE</Link></li>
                    <li><Link to="/documentation/doc_measurement#top">MEASUREMENT</Link></li>
                    <li><Link to="/documentation/doc_money#top">MONEY</Link></li>
                    <li><Link to="/documentation/doc_payment#top">PAYMENT_CARD</Link></li>

                    {/* redundant not useful links to sub-types should be sub-list*/}
                    <li><Link to="/documentation/doc_nationality#top">NATIONALITY</Link></li>
                    <li><Link to="/documentation/doc_nationality#top">ETHNICITY</Link></li>
                    <li><Link to="/documentation/doc_nationality">LANGUAGE</Link></li>

                    {/* redundant not useful links to sub-types should be sub-list*/}
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/COMPANY</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/COMPANY/TICKER</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/CRIMINAL</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/EDUCATIONAL</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/GOVERNMENT</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/PERFORMING</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/SPORTS</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/NEWS</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/SOCIALMEDIA</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/FUND</Link></li>
                    <li><Link to="/documentation/doc_organization#top">ORGANIZATION/TRUST</Link></li>

                    <li><Link to="/documentation/doc_person">PERSON</Link></li>
                    <li><Link to="/documentation/doc_phone">PHONE</Link></li>
                    <li><Link to="/documentation/doc_position">POSITION</Link></li>

                    {/* redundant not useful links to sub-types should be sub-list*/}
                    <li><Link to="/documentation/doc_thing">THING</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/CHEMICAL</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/MEDICAL</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/FINANCIAL</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/LEGAL</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/EVENT</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/FACILITY</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/PRODUCT</Link></li>
                    <li><Link to="/documentation/doc_thing">THING/TECHNOLOGY</Link></li>

                    <li><Link to="/documentation/doc_twitter">TWITTER</Link></li>
                    <li><Link to="/documentation/doc_uri">URI</Link></li>
                </ul>

                <h4 className="heading-3" id="enrichv2_topic_models">Topic Models</h4>
                <table className="table-wrapper">
                    <thead className="table-header">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                        </tr>
                    </thead>
                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">news</td>
                            <td className="meaning">A 200 topic model for global news (used to build the Finch Entity Knowledgebase).</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_summarize_models">Summary Models</h4>
                <table className="table-wrapper">
                    <thead className="table-header">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">News</td>
                            <td className="meaning">Hybrid news summarization model trained on a diverse corpus of news (recommended model for news).</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_sentiment_models">Sentiment Models</h4>
                {/* THIS NEEDS CHECKED */}
                <div className="description enrich">
                <div className="note">
                    <span className="fa fa-info-circle fa-xl">&nbsp;</span>
                    NOTE: The chosen model will dictate the allowed values the sentiment <i>type</i> can hold in the response and whether a <i>score</i> will be provided.
                </div>
                </div>
                <table className="table-wrapper">
                    <thead className="table-header">
                        <tr>
                            <th>Name </th>
                            <th>Entity-Level and Mention-Level </th>
                            <th>Document-Level and Sentence-Level </th>
                            <th>Description </th>
                            <th>Sentiment Types </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                    {/* <tr>
                        <td className="attribute">news3</td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="meaning">A 3-class sentiment model applicable to news content.<br/><br/>
                            This model produces a sentiment score value.
                        </td>
                        <td className="type">positive,neutral,<br/>negative</td>
                    </tr> */}
                    {/* not built can't be used */}
                    {/* <tr> 
                        <td className="attribute">earningsCall</td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="meaning">A 3-class sentiment model applicable to earnings calls.<br/><br/>
                            This model produces a sentiment score value.
                        </td>
                        <td className="type">positive,neutral,<br/>negative</td>
                    </tr> */}
                    {/* <tr>
                        <td className="attribute">financial3</td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="meaning">A 3-class sentiment model applicable to financial content.<br/><br/>
                            This model produces a sentiment score value.
                        </td>
                        <td className="type">positive,neutral,<br/>negative</td>
                    </tr> */}
                    <tr>
                        <td className="attribute">financialTopics</td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="type"><span className="fa fa-check"/></td>
                        <td className="meaning">
                            A 3-class sentiment model that combines topic modeling, with LDA, and 
                            deep transfer learning to achieve state-of-the-art results for document-level, sentence-level and entity-level financial news sentiment analysis and event detection.<br/><br/>
                            This model produces a sentiment score value, predicted probabilities for each sentiment class, and a label for the dominant event or topic of the document.
                        </td>
                        <td className="type">positive<br/>neutral<br/>negative</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_salience_models">Salience Models</h4>
                <table className="table-wrapper">
                    <thead className="table-header">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">News</td>
                            <td className="meaning">A salience model that represents current news.
                            </td>
                        </tr>
                    </tbody>
                </table>
                
                <h4 className="heading-3" id="tag_models">Tag Models</h4>
                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Support Pos. Threshold </th>
                        <th>Support Neg. Threshold </th>
                        {/* may want to consider removing negative threshold if none of the models use it, or just comment it out */}
                    </tr>
                    </thead>
                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">iptc</td>
                        <td className="meaning">The IPTC application classifies news articles using the 
                        International Press Telecommunications Council media topics subject taxonomy, 
                        which has 1,100 nodes.  This is a multi-label classifier, which may return 
                        several tags, representing IPTC topics, for a given news article.</td>
                        <td><span className="fa fa-check"></span></td>
                        <td></td>
                    </tr>
                    <tr>
                        <td className="attribute">biztop</td>
                        <td className="meaning">Business Topics model tags documents with topics based 
                            on business events or signals described within the document. Each tag is 
                            further classified into a set of sub-topics.</td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr>
                        <td className="attribute">esg</td>
                        <td className="meaning">An application to curate news feed content related to "ESG": Environmental, 
                        Social, and Governance business topics. The application uses a combination of machine learning and 
                        transformer models to tag at 26 sub-levels in the ESG hierarchy as defined by the Sustainability Accounting 
                        Standards Board. The threshold is used both as a final filter to exclude low-scoring tags from the output 
                        and as an internal computation to aggregate tags found for multiple sentences.  This means that raising 
                        or lowering the threshold may change internal scoring, causing different tags to be eligible for inclusion 
                        in the output.  Still, only tags with final scores above the given threshold will be returned.</td>
                        <td><span className="fa fa-check"></span></td>
                        <td></td>
                    </tr>
                    </tbody>
                </table>
                
                <h4 className="heading-3" id="enrichv2_text_corrections">Text Corrections</h4>
                <table className="table-wrapper">
                    <thead className="table-header">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                        </tr>
                    </thead>
                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">case</td>
                            <td className="meaning">Fix capitalization issues in the input string.</td>
                        </tr>
                        <tr>
                            <td className="attribute">punctuation</td>
                            <td className="meaning">Fix punctuation issues in the input string.</td>
                        </tr>
                        <tr>
                            <td className="attribute">token</td>
                            <td className="meaning">Reviews each token in the input string to determine if, it is the result of erroneously dropping white space separating two consecutive words. If so, splits the token into the two component words.</td>
                        </tr>
                    </tbody>
                </table>

                {/* <h4 className="heading-3" id="enrichv2_gdm_modules">GDM Modules</h4>
                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                    </tr>
                    </thead>
                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">base</td>
                        <td className="meaning">The base GDM module with over 30 million places.</td>
                    </tr>
                    <tr>
                        <td className="attribute">ihs</td>
                        <td className="meaning">IHS oil and gas GDM module. Includes the base GDM module.</td>
                    </tr>
                    </tbody>
                </table> */}

                <h4 className="heading-2" id="enrichv2_response">Response</h4>
                <div className="description enrich">The enrich response is a JSON Object, and is determined by the service and features specified at the time of the request.</div>
                    {/* The response output format is organized as follows:

                    <div className="bullet-list">
                        <ul>
                            <li>Entities - An ordered array of the extracted entities.</li>
                            <div className="bullet-list-l2">
                                <ul>
                                    <li>Mentions - An array of the extracted entity text and positions.</li>
                                    <div className="bullet-list-l2">
                                        <ul>
                                            <li>Mention sentiment - The sentiment attributed to a particular mention.</li>
                                        </ul>
                                    </div>
                                    <li>Entity sentiment - The sentiment attributed to the entity.</li>
                                    <li>Disambiguated - An object uniquely identifying the entity as 
                                        an entry in the Finch Knowledgebase.
                                    </li>
                                </ul>
                            </div>
                            <li>Document sentiment - An object denoting the document and sentence  
                                sentiment.
                            </li>
                            <div className="bullet-list-l2">
                                <ul>
                                    <li>Sentence sentiment - The sentiment attributed to a particular sentence.</li>
                                </ul>
                            </div>
                            <li>Topic vector - A data structure that can be used to measure document 
                                similarity.
                            </li>
                            <li>Key phrases - An array of text key phrases assigned to the document.</li>
                            <li>Key phrase weights - An array of doubles that define importance for 
                                each corresponding phrase in key phrases.
                            </li>
                            <li>Industry - A data structure that contains salience and aggregated sentiment for industry groups, 
                                economic and business sectors contained in the document.
                            </li>
                            <li>relationships - An array of entity relationships extracted from the document.</li>
                            <li>tagResults - An array of tags (by model) extracted from the document.</li>
                            <li>salience - Contains salience information from the document.</li>
                            <li>documentSummary - Contains summary information pertaining to the document.</li>
                        </ul>
                    </div> 
                </div>*/}

                <h4 className="heading-3" id="enrichv2_response_object">Response JSON Object</h4>
                <div className="note">
                    <span className="fa fa-info-circle fa-xl">&nbsp;</span>
                    NOTE: Fields that are always present in the Response JSON Object, Entity JSON Object or one of the otherwise nested JSON Objects are denoted with a dagger (†).
                </div>
                <div className="description enrich"></div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">documentId</td>
                            <td className="meaning">A client supplied tracking identifier. This value is included in the response when available.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">entities</td>
                            <td className="meaning">An ordered list of entities detected in the document.</td>
                            <td className="type"><a href="#enrichv2_entity_object">Entity JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">keyPhrases</td>
                            <td className="meaning">
                                List of key phrases discovered from the document.<br/><br/>
                                Included if <i>keyPhrases</i> was true in the request.
                            </td>
                            <td className="type">String Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">keyPhraseWeights</td>
                            <td className="meaning">
                                List of decimals that define importance to each corresponding key phrase in <i>keyPhrases</i>.<br/><br/>
                                Included if <i>keyPhrases</i> was true in the request.
                            </td>
                            <td className="type">Double Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">requestId †</td>
                            <td className="meaning">A unique tracking identifier assigned by the enrich service.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">sentiment</td>
                            <td className="meaning">
                                Contains sentence-level and document-level sentiment enrichments produced by the provided <a href="#sentiment_models"><i>sentimentModel</i></a>.
                                Included only if <i>sentiment</i> was true in the request and entity relationships were found in the document.    
                            </td>
                            <td className="type"><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">text</td>
                            <td className="meaning">
                                The UTF-8 encoded document that was enriched.<br/><br/>
                                Included if <i>returnText</i> was true in the request.
                            </td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">topicVector</td>
                            <td className="meaning">Contains the document-level topics and weights. Used to measure the similarity between documents in a corpus.</td>
                            <td className="type"><a href="#enrichv2_topic_vector_object">Topic Vector JSON Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">industries</td>
                            <td className="meaning">
                                Contains salience and aggregated sentiment information for economic sectors, business sectors and industry groups.<br/><br/>
                                Included only if <i>salience</i> was true in the request.<br/><br/>
                                Sentiment included only if <i>entitySentiment</i> was true in the request and sentiment model was <i>financial3</i> or <i>financialTopics</i>.                        
                            </td>
                            <td className="type"><a href="#enrichv2_industries_object">Industry JSON Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">relationships</td>
                            <td className="meaning">
                                List that contains information on entity relationships extracted from the document.<br/><br/>
                                Included only if <i>relationships</i> was true in the request and entity relationships were found in the document.
                            </td>
                            <td className="type"><a href="#enrichv2_relationship_object">Relationship JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">tagResults</td>
                            <td className="meaning">
                                Contains tag enrichments extracted from the document produced by the provided <a href="#tag_models"><i>tagModels</i></a>.<br/><br/>
                                Included only if <i>tags</i> was true in the request and tags were found in the document by the model.
                            </td>
                            <td className="type"><a href="#enrichv2_tagmodel_object">Tag Model JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">salience</td>
                            <td className="meaning">
                                Contains document-level salience information.<br/><br/>
                                Included only if <i>sentenceSalience</i> was true in the request.
                            </td>
                            <td className="type"><a href="#enrichv2_salience_object">Salience Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">documentSummary</td>
                            <td className="meaning">
                                Contains the summarized document produced by the provided <a href="#enrichv2_summarize_models"><i>summaryModel</i></a>.<br/><br/>
                                Included only if <i>documentSummarization</i> was true in the request.
                            </td>
                            <td className="type"><a href="#enrichv2_document_summary_object">Document Summary JSON Object</a></td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_entity_object">Entity JSON Object</h4>
                <p className="description enrich">This identifies an "entity" detected in the document.</p>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">attributes</td>
                            <td className="meaning">
                                Contains attributes related to the entity extraction.<br/><br/>
                                {/* Should I include link here to Knowledgebase Entities */}
                                See <a href="#enrichv2_entity_types">Entity Types</a> and related Knowledgebase Entity attributes
                            </td>
                            <td className="type">JSON Object</td>
                        </tr>
                        <tr>
                            <td className="attribute">count *</td>
                            <td className="meaning">The number of mentions attributed to this entity.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">disambiguated</td>
                            <td className="meaning">Contains enrich service prediction of the matching disambiguated entity based on document and context.</td>
                            <td className="type"><a href="#enrichv2_disambiguation_object">Disambiguation JSON Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">label</td>
                            <td className="meaning">The preferred name for display purposes of the disambiguated entity.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">mentions</td>
                            <td className="meaning">List of entity mentions identifying extracted string and respective position in the document.</td>
                            <td className="type"><a href="#enrichv2_mention_object">Mention JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">sentiment</td>
                            <td className="meaning"> Contains an overall entity sentiment based on all mentions of the entity in the document.</td>
                            <td className="type"><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">type</td>
                            <td className="meaning">Identifies the disambiguated entity type. Subtypes, when available, are delimited by a forward slash, '/', character.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">unrelated</td>
                            <td className="meaning">Present and true if all mentions of this entity are deemed unrelated to the overall narrative of the document; otherwise this element is omitted.</td>
                            <td className="type">Boolean</td>
                        </tr>
                        <tr>
                            <td className="attribute">salienceScore</td>
                            <td className="meaning">Entity-level salience score that indicates the importance or centrality of that entity to the entire document.</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">internalRef</td>
                            <td className="meaning">
                                An identifier used to locate this entity in  <i>relationships</i> and in <i>entities</i>.<br/><br/>
                                Included when <i>relationships</i> is true in the request.
                            </td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">entitySummary</td>
                            <td className="meaning">List of summarized sentences. Included when <i>entitySummarization</i> is true in the request.</td>
                            <td className="type">String Array</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_mention_object">Mention JSON Object</h4>
                <p className="description enrich">Contains a singular instance of an entity being mentioned in the document including where it was, the sentiment of the mention, and the confidence in extracting the text and disambiguating the entity.</p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example: <br/>
                    Richard Nixon was an American president who oversaw the thawing of relations with China. President Nixon also oversaw the peaceful desegregation of southern schools. Nixon's presidency, however, ended in scandal and impeachment.<br/><br/>
                    In this document there are 3 mentions of the entity "Richard Nixon": "Richard Nixon", "President Nixon", and "Nixon's"<br/>
                    Each mention would have a string extracted from the document, a position, and other associated data and scores/confidences for the enrichments performed. 
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">disambiguatedConfidence</td>
                            <td className="meaning">The confidence score associated with the disambiguation.</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">extractedText</td>
                            <td className="meaning">The string extracted from the document.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">extractedConfidence</td>
                            <td className="meaning">The confidence score associated with the entity extraction.</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">position</td>
                            <td className="meaning">List of two integers that identify the zero-based starting and ending character positions of the <i>extractedText</i> in the document.</td>
                            <td className="type">Integer Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">sentiment</td>
                            <td className="meaning">When requested, the enrich service will determine the sentiment associated with this entity mention.</td>
                            <td className="type"><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a></td>
                        </tr>
                        <tr>
                            <td className="attribute">unrelated</td>
                            <td className="meaning">Present and true if the particular mention is unrelated to the overall narrative of the document; otherwise this element is omitted.</td>
                            <td className="type">Boolean</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_disambiguation_object">Disambiguation JSON Object</h4>
                <p className="description enrich">
                    Contains the information linking an ambiguous entity mention in the text, to a specific and unambiguous entry in the Finch Knowledgebase. <br/><br/>
                    Disambiguation results are scored based on a complex variety of factors including entity attributes, news prominence, topic vectors, and proximity contexts like industry, location, or other distinguishing mentions.<br/><br/>
                    You can find supported fields documented <Link to="/documentation/doc_kbattributes#top">here</Link>.
                </p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example: <br/>
                    Apple Inc., the technology company known for the iPhone and MacBooks, saw a rise in stock prices today, while Apple Bank, the regional financial institution based in New York, reported steady growth.<br/><br/>
                    "Apple Inc" is disambiguated with id "13122678685698"<br/>
                    "Apple Bank" is disambiguated with id "4629865431042"<br/>
                    "New York" is disambiguated as the state with "277151744" and has locational attributes associated with its disambiguated result.
                </div>
                <div className="note">
                    <span className="fa fa-info-circle fa-xl">&nbsp;</span>
                    NOTE: If <i>disambiguateFields</i> is provided in the request then this may also contain provided fields pulled from the knowledgebase.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">attributes</td>
                            <td className="meaning">Contains reference data for the disambiguated entity.</td>
                            <td className="type">
                                JSON Object<br/><br/>
                                {/* Should I include link here to Knowledgebase Entities */}
                                See <a href="#enrichv2_entity_types">Entity Types</a> and related Knowledgebase Entity attributes
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">id</td>
                            <td className="meaning">The unique identifier assigned to a disambiguated entity, and can be used to find the entity or additional fields Finch Knowledgebase.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">label</td>
                            <td className="meaning">The preferred name for display purposes of the disambiguated entity.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">score</td>
                            <td className="meaning">The confidence score associated with the disambiguation result.</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">permIds</td>
                            <td className="meaning">List with London Stock Exchange Group information for Perm Ids</td>
                            <td className="type">JSON Object Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">sicCodes</td>
                            <td className="meaning">List with SEC information for Standard Industrial Classification.</td>
                            <td className="type">JSON Object Array</td>
                        </tr>
                        {/*! Need to look at why this links to knowledgebase entity fields. There seems to be a lot of redundant information between the enrich page here and that page.
                            That page also uses **/}
                        <tr>
                            <td className="attribute">stocks</td>
                            <td className="meaning">List with stock information including exchanges, tickers, etc.</td>
                            <td className="type"><Link to="/documentation/doc_kbattributes#stocks">Stocks</Link></td>
                        </tr>
                        <tr>
                            <td className="attribute">location</td>
                            <td className="meaning">Contains location information for a GEO entity.</td>
                            <td className="type"><Link to="/documentation/doc_kbattributes#location">Location JSON Object</Link></td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_sentiment_object">Sentiment JSON Object</h4>
                <p className="description enrich">Contains information on the document-level, entity-level, or sentence-level sentiment.</p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example: <br/>
                    Richard Nixon was an American president who oversaw the thawing of relations with China. He also oversaw the peaceful desegregation of southern schools. His presidency, however, ended in scandal and impeachment.<br/><br/>
                    The Sentiment JSON Object for the overall document may have a Neutral class since there are both positive and negative elements present.<br/><br/>
                    The Sentiment JSON Object for Richard Nixon may have a Neutral class since for the same reason.<br/><br/>
                    The Sentiment JSON Object for the first and second sentences or mentions of Richard Nixon may have a Positive class.<br/>
                    The Sentiment JSON Object for the third sentence or mention of Richard Nixon may have a Negative class.<br/>
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">class</td>
                            <td className="meaning">The overall Positive, Negative, or Neutral sentiment(s) assigned at the document-level, sentence-level, or mention-level.</td>
                            <td>
                                String<br/><br/>
                                See <a href="#enrichv2_sentiment_models">Sentiment Models</a> for list of supported sentiment classes for the requested model.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">classConfidence</td>
                            <td className="meaning">The confidence score associated with the sentiment class.</td>
                            <td>
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">position</td>
                            <td className="meaning">
                                A list of two integers that are the zero-based starting and ending positions of the string in the document.<br/><br/>
                                NOTE: Applicable only to sentence sentiment.
                            </td>
                            <td>Integer Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">score</td>
                            <td className="meaning">
                                Optional. Sentiment score expressed as a decimal number, with 0.0 being neutral.<br/><br/>
                                The selected <a href="#enrichv2_sentiment_models">sentiment model</a> will determine if this value is available.
                                </td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [-1.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">probabilities</td>
                            <td className="meaning">
                                Optional. List of decimal numbers that represent the predicted probabilities for each sentiment class.<br/><br/>
                                NOTE: Applicable only for <i>financialTopics</i> <a href="#sentiment_models">sentiment model</a>.
                            </td>
                            <td className="type">Decimal Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">topic</td>
                            <td className="meaning">
                                Optional. The label for the dominant event or topic of the document.<br/><br/>
                                NOTE: Applicable only for <i>financialTopics</i> <a href="#sentiment_models">sentiment model</a>.<br/><br/>
                                See <a href="#financial_topics">Financial Topics</a>
                            </td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">sentences</td>
                            <td className="meaning">
                                List of sentences the document is divided into.<br/><br/>
                                NOTE: Applicable only to <i>documentSentiment</i>. {/* what does this mean? Is it just that there is only sentiment information included in this? */}
                            </td>
                            <td><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">text</td>
                            <td className="meaning">
                                The sentence string to which the sentiment is attributed.<br/><br/>
                                NOTE: Applicable only to sentence sentiment.
                            </td>
                            <td>String</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="financial_topics">Financial Topics</h4>
                <div className="description enrich">Valid <i>topic</i> values for financial Sentiment Sentiment financial topic values are listed below.</div>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">Bankruptcy</td>
                            <td className="meaning">Content related to bankruptcies, debt restructuring, chapter 7, chapter 11 or other discussion about entities seeking relief from some or all of their debts.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Buy-Rating</td>
                            <td className="meaning">Content related to analysts when they anticipate that an asset’s price will be changing from its current level over a period of time.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Demand</td>
                            <td className="meaning">Content related to demand for a company's goods and services at given prices.</td>
                        </tr>                    
                        <tr>
                            <td className="attribute">Dividend</td>
                            <td className="meaning">Content related to the distribution of profits by a corporation to its shareholders.</td>
                        </tr>  
                        <tr>
                            <td className="attribute">Earnings-Estimate</td>
                            <td className="meaning">Content related to an analyst's estimate for a company's future quarterly or annual earnings per share.</td>
                        </tr> 
                        <tr>
                            <td className="attribute">Economic-Recession</td>
                            <td className="meaning">Content related to stagnant economic activity, contraction in the business cycle or over-supply of goods compared to its demand.</td>
                        </tr> 
                        <tr>
                            <td className="attribute">Economic-Recovery</td>
                            <td className="meaning">Content related to the business cycle following a recession, during which an economy recovers and exceeds levels prior to downturn.</td>
                        </tr> 
                        <tr>
                            <td className="attribute">Growth</td>
                            <td className="meaning">Content related to an increase in the production/consumption of goods and services of a company or an economy.</td>
                        </tr> 
                        <tr>
                            <td className="attribute">Initial-Public-Offering</td>
                            <td className="meaning">Content related to the process of offering shares of a private corporation to the public in a new stock issuance.</td>
                        </tr>                    	
                        <tr>
                            <td className="attribute">Job-Cuts</td>
                            <td className="meaning">Content related to reduction in the number of jobs in an organization.</td>
                        </tr> 	
                        <tr>
                            <td className="attribute">Joint-Venture</td>
                            <td className="meaning">Content related to a joint venture or partnership between two or more businesses.</td>
                        </tr> 
                        <tr>
                            <td className="attribute">Lawsuit</td>
                            <td className="meaning">Content related to legal action by one person or entity against another person or entity.</td>
                        </tr> 	
                        <tr>
                            <td className="attribute">Mergers-Acquisitions</td>
                            <td className="meaning">Content related to consolidation of companies or assets through various types of financial transactions.</td>
                        </tr> 	
                        <tr>
                            <td className="attribute">Price-Margin</td>
                            <td className="meaning">Content related to a difference between a product or service's selling price and the cost of production, or the ratio of profit to revenue.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Profit-Loss</td>
                            <td className="meaning">Content related to a profit or loss incurred by the business during the particular accounting period.</td>
                        </tr>		
                        <tr>
                            <td className="attribute">Quarter-Earnings</td>
                            <td className="meaning">Content related to a company's quarterly earnings and the paying of dividends.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Sales-Revenue</td>
                            <td className="meaning">Content related to the sales or revenue received by a company.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Sales-Volume</td>
                            <td className="meaning">Content related to the number of units sold during a specific accounting period.</td>
                        </tr>		
                        <tr>
                            <td className="attribute">Share-Price</td>
                            <td className="meaning">Content related to the price of an individual share in a company.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Stock-Market</td>
                            <td className="meaning">Content related to stock markets and exchanges where stocks and bonds are traded.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Unemployment</td>
                            <td className="meaning">Content related to jobs and unemployment.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Venture-Capital</td>
                            <td className="meaning">Content related to private equity or other types of financing that investors provide to startup companies and businesses that are believed to have long-term growth potential.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Other-Business</td>
                            <td className="meaning">Content related to business topics not specifically covered by other topics.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Entertainment</td>
                            <td className="meaning">Non-business content related to entertainment.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Politics</td>
                            <td className="meaning">Non-business content related to politics.</td>
                        </tr>		
                        <tr>
                            <td className="attribute">Sports</td>
                            <td className="meaning">Non-business content related to sports.</td>
                        </tr>	
                        <tr>
                            <td className="attribute">Technology</td>
                            <td className="meaning">Non-business content related to technology.</td>
                        </tr>		
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_topic_vector_object">Topic Vector JSON Object</h4>
                <div className="description enrich">
                    Contains a set of weighted topics that represents the content of an article of document. It can be used to 
                    measure similarity between documents. The similarity score can then be used for several purposes, such as 
                    recommending related content.
                </div>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">ids</td>
                            <td className="meaning">List of topic IDs assigned to the document. The each ID has a respective entry in the <i>weights</i> list at the same index.</td>
                            <td className="type">Integer Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">weights</td>
                            <td className="meaning">List of weights assigned to the corresponding topic ID at the same index in the <i>ids</i> list.</td>
                            <td className="type">Decimal Array</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_salience_object">Salience JSON Object</h4>
                <p className="description enrich">
                    This contains the salience information found in the document.
                    Salience is the measure of how central, noteworthy, or important, a sentence, or entity is in representing the key ideas of a document.
                </p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example:<br/> 
                    In an article about the Watergate Scandal multiple things may be discussed including contemporary context, however:<br/><br/>
                    <span className="fa fa-check fa-xl">&nbsp;</span>Sentences pertaining to the burglary would likely be more salient and have higher scores.<br/>
                    <span className="fa fa-close fa-xl">&nbsp;&nbsp;</span>Sentences about 1970s offices using bold colors would likely be less salient and have lower scores.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">sentences</td>
                            <td className="meaning">List of sentences in the document with their associated salience score.</td>
                            <td className="type"><a href="#enrichv2_sentence_object">Sentence JSON Object</a> Array</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="enrichv2_sentence_object">Sentence JSON Object</h4>
                <p className="description enrich">Contains the information for a sentence.</p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example: <br/>
                    Ulysses S. Grant was a successful general during the Civil War. He was critical in victories in the West and the South. The animated show The Simpson is not named after him as his middle initial did not stand for anything. The Civil War was America's deadliest conflict.<br/><br/>
                    In this document there are 4 sentences.<br/>
                    The text of the first sentence begins at the document's start, and ends at the period. Each subsequent sentence starts after the period (excluding whitespace) and ends at the period.<br/>
                    The score for each sentence represents the salience or importance of the sentence, the <i>score</i> for the sentence about The Simpson is likely lower than the others.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">text</td>
                            <td className="meaning">The string representation of the sentence.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">score</td>
                            <td className="meaning">The salience score associated with the sentence.</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_industries_object">Industry JSON Object</h4>
                <p className="description enrich">
                    Contains information on salience and aggregated sentiment information contained in the document for different industry groups, as well as economic and business sectors.
                    {/* Do we want explanation of salience here? Salience indicates how relevant the text of the document is to the industry group. */}
                </p>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">economicSectors</td>
                            <td className="meaning">List of economic sectors found in the document.</td>
                            <td className="type"> <a href="#enrichv2_economicsector_type">Economic Sector JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">businessSectors</td>
                            <td className="meaning">List of business sectors found in the document.</td>
                            <td className="type"><a href="#enrichv2_businesssector_type">Business Sector JSON Object</a> Array</td>
                        </tr>
                        <tr>
                            <td className="attribute">industryGroups</td>
                            <td className="meaning">List of industry groups found in the document.</td>
                            <td className="type"><a href="#enrichv2_industrygroup_type">Industry Group JSON Object</a> Array</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_economicsector_type">Economic Sector JSON Object</h4>
                <p className="description enrich">
                    Contains salience and aggregated sentiment of economic sectors mentioned in the document.<br/><br/>
                    Economic sectors refer to a broad category of industries with similar characteristics within an economy, such as primary, secondary, or tertiary.Economic sectors may encompass multiple business sectors.
                </p>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">economicSector</td>
                            <td className="meaning">The name of the economic sector.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">salience</td>
                            <td className="meaning">A string representation of the salience.</td>
                            <td className="type">
                                String<br/><br/>
                                The string will be a decimal number between 0 and 1.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">sentiment</td>
                            <td className="meaning">Contains aggregated sentiment of the economic sector. <br/><br/>
                                NOTE: Applicable only if <i>entitySentiment</i> is true in the request and sentimentModel set to one of <i>financial3</i> or <i>financialTopics</i>.</td>
                            <td className="type"><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a></td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_businesssector_type">Business Sector JSON Object</h4>
                <p className="description enrich">
                    Contains salience and aggregated sentiment of business sectors mentioned in the document.<br/><br/>
                    A business sector is a group of companies that are involved in similar economic activities, such as production, services, or the natural resource extraction. Business sectors may encompass multiple industry groups.
                </p>
                
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">businessSector</td>
                            <td className="meaning">The name of the business sector.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">salience</td>
                            <td className="meaning">A string representation of the salience.</td>
                            <td className="type">
                                String<br/><br/>
                                The string will be a decimal number between 0 and 1.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">sentiment</td>
                            <td className="meaning">Contains aggregated sentiment of the business sector. <br/><br/>
                                NOTE: Applicable only if <i>entitySentiment</i> is true in the request and sentiment model set to one of <i>financial3</i> or <i>financialTopics</i>.</td>
                            <td className="type"><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_industrygroup_type">Industry Group JSON Object</h4>
                <p className="description enrich">
                    Contains salience and aggregated sentiment of industry groups mentioned in the document.<br/><br/>
                    An industry group is a grouping of companies that share similar products, services, or business operations.
                </p>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">industryGroup</td>
                            <td className="meaning">The name of the industry group.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">salience</td>
                            <td className="meaning">A string representation of the salience.</td>
                            <td className="type">
                                String<br/><br/>
                                The string will be a decimal number between 0 and 1.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">sentiment</td>
                            <td className="meaning">Contains aggregated sentiment of the industry group. <br/><br/>
                                NOTE: Applicable only if <i>entitySentiment</i> is true in the request and sentiment model set to one of <i>financial3</i> or <i>financialTopics</i>.</td>
                            <td className="type"><a href="#enrichv2_sentiment_object">Sentiment JSON Object</a></td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_document_summary_object">Document Summary JSON Object</h4>
                <p className="description enrich">Contains the summarized document information produced by the provided <a href="#enrichv2_summarize_models"><i>summaryModel</i></a>.</p>
            

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">summary</td>
                            <td className="meaning">
                                {/* check that this is correct and is not intended for targetLanguage */}
                                The summary of the document.<br/><br/>
                                Currently summary will exist for <i>en</i> documents only.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            {/* add a brief example */}
                            <td className="attribute">summarySentences</td>
                            <td className="meaning">List of summarizing sentences extracted from the document.</td>
                            <td className="type">String Array</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_relationship_object">Relationship JSON</h4>
                <p className="description enrich">
                    Contains information on relationships between entities found in the document.<br/><br/>
                    Entity relationships are structured as a Subject, Predicate, Object triple, and are always read in that order. See {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/Semantic_triple')}.
                </p>
                <div className="note">
                    <span className="fa fa-info-circle fa-xl">&nbsp;</span>
                    NOTE: The "Object of a Relationship" should not be confused with "JSON Object". To avoid confusion in this section we will only use "JSON" when referring to "JSON Objects".
                </div>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">confidence</td>
                            <td className="meaning">A measure of the confidence that the extracted relationship is correct</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">evidence</td>
                            <td className="meaning">Part of the document where the relationship was found.</td>
                            <td className="type"><a href="#enrichv2_relationship_evidence_type">Evidence of a Relationship JSON</a>.</td>
                        </tr>
                        <tr>
                            <td className="attribute">subject</td>
                            <td className="meaning">The subject of the relationship.</td>
                            <td className="type"><a href="#enrichv2_relationship_subject_type">Subject of a Relationship JSON</a>.</td>
                        </tr>
                        <tr>
                            <td className="attribute">predicate</td>
                            <td className="meaning">The predicate of the relationship.</td>
                            <td className="type"><a href="#enrichv2_relationship_predicate_type">Predicate of a Relationship JSON</a>.</td>
                        </tr>
                        <tr>
                            <td className="attribute">object</td>
                            <td className="meaning">The object of the relationship.</td>
                            <td className="type"><a href="#enrichv2_relationship_object_type">Object of a Relationship JSON</a></td>
                        </tr>                        
                    </tbody>
                </table>

                <h4 className="heading-4" id="enrichv2_relationship_evidence_type">Evidence of a Relationship JSON</h4>
                <p className="description enrich">This defines information pertaining to the where in the document a relationship was found.</p>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">sentence</td>
                            <td className="meaning">The sentence in the document that contains the relationship.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">start</td>
                            <td className="meaning">The starting index of the <i>sentence</i> in the document.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">stop</td>
                            <td className="meaning">The ending index of the <i>sentence</i> in the document.</td>
                            <td className="type">Integer</td>
                        </tr>
                    </tbody>
                </table>   

                <h4 className="heading-4" id="enrichv2_relationship_subject_type">Subject of a Relationship</h4>
                <p className="description enrich">This defines the <i>subject</i> of the relationship. The subject is the main entity being discussed in a triple.</p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example:<br/>
                    <u>Grace Hopper</u> is the <u>Subject</u> of this triple: (Grace Hopper) ==(Educated-at)==&gt; (Yale University)<br/>
                    <u>Los Angeles Lakers</u> is the <u>Subject</u> of this triple: (Los Angeles Lakers) ==(Has-member)==&gt; (LeBron James)<br/>   
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">anchor</td>
                            <td className="meaning">The <i>extractedText</i> from the entity <i>mentions</i> list that represents the object of the relationship.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">id</td>
                            <td className="meaning">The unique identifier assigned to a disambiguated entity, and can be used to find the entity or additional fields Finch Knowledgebase.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">internalRef</td>
                            <td className="meaning">An identifier used to locate this entity in  <i>entities</i> and in <i>relationship</i>.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">start</td>
                            <td className="meaning">The starting index of the <i>extractedText</i> in the document.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">stop</td>
                            <td className="meaning">The ending index of the <i>extractedText</i> in the document.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">type</td>
                            <td className="meaning">
                                A string value identifying the disambiguated entity type. Subtypes, when available, are delimited by a forward slash, '/', character.<br/><br/>
                                Identical to <i>type</i> in <a href="#enrichv2_entity_object">Entity JSON</a>
                            </td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">label</td>
                            <td className="meaning">The preferred name for display purposes of the disambiguated entity.</td>
                            <td className="type">String</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4 className="heading-4" id="enrichv2_relationship_predicate_type">Predicate of a Relationship</h4>
                <p className="description enrich">
                    This defines the predicate of the relationship.<br/><br/>
                    The predicate is the relationship or type of relationship between the subject and object in a triple.
                </p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example:<br/>
                    <u>Relative-of</u> is the <u>Predicate</u> of this triple: (Aunt) ==(Relative-of)==&gt; (Dad)<br/>
                    <u>Founder-of</u> is the <u>Predicate</u> of this triple: (Steve Jobs) ==(Founder-of)==&gt; (Apple Inc.)
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">type</td>
                            <td className="meaning">The relation type.</td>
                            <td className="type">
                                String<br/><br/>
                                See <a href="#enrichv2_relationship_types">Relationship Types</a> for available values.
                        </td>
                        </tr>
                        {/* does this also go in predicate properties json? */}
                        <tr>
                            <td className="attribute">bidirectional</td>
                            <td className="meaning">
                                Indicates whether the relationship goes both ways.<br/><br/>
                                If true the object and subject are interchangeable for the given relationship, e.g. Partner-of.<br/><br/>
                                If false the object and subject are distinct for the given relationship and cannot be interchanged for one another, e.g. Father-of.
                            </td>
                            <td className="type">Boolean</td>
                        </tr>
                        <tr>
                            <td className="attribute">properties</td>
                            <td className="meaning">Additional properties related to the predicate.</td>
                            <td className="type"><a href="#enrichv2_relationship_predicate_properties_type">Predicate Properties JSON</a></td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="enrichv2_relationship_predicate_properties_type">Relationship Predicate Properties OBJECT</h4>
                {/* what makes them optional */}
                <p className="description enrich">Contains additional optional properties or attributes related to the predicate of a relationship.</p>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">status</td>
                            <td className="meaning">
                                The status of the relationship.<br/><br/>
                                Possible values:<br/>
                                <i>Current</i> - The relationship is new or current<br/>
                                <i>Potential</i> - The relationship has not happened yet<br/>
                                <i>Ended</i> - The relationship has ended.
                            </td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">position</td>
                            <td className="meaning">Title or position of a person within an organization.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">value</td>
                            <td className="meaning">A numeric value associated with the relationship.</td>
                            <td className="type">String</td>
                        </tr>
                        {/*
                        <tr>
                            <td className="attribute">Organization-Location</td>
                            <td className="meaning">Attribute information for the Organization-Location type.
                            </td>
                            <td className="type">String<br/>
                                <br/>
                                Possible values:<br/>
                                <i>Founded-In</i>, <i>Headquartered-In</i>, <i>Operates-In</i> 
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">Person-Location</td>
                            <td className="meaning">Attribute information for the Person-Location type.
                            </td>
                            <td className="type">String<br/>
                                <br/>
                                Possible values:<br/>
                                <i>Died-In</i>, <i>Born-In</i>, <i>Born-And-Died-In</i>, <i>Citizen-Of</i>, <i>Resident-Of</i>
                            </td>
                        </tr>
                        */}
                        <tr>
                            <td className="attribute">Relative-of</td>
                            <td className="meaning">
                                Attribute information for the Relative-of type.<br/><br/>
                                Possible values:<br/>
                                <i>Parent-Of</i><br/>
                                <i>Child-Of</i><br/>
                                <i>Sibling-Of</i><br/>
                                <i>Spouse/Partner-Of</i>
                            </td>
                            <td className="type">String</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4 className="heading-4" id="enrichv2_relationship_object_type">Object of a Relationship JSON</h4>
                <p className="description enrich">
                    This defines the <i>object</i> of the relationship.<br/><br/>
                    This is the entity that is connected to the <i>subject</i> through the <i>predicate</i>.
                </p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example:<br/>
                    <u>Abraham Lincoln</u> is the <u>Object</u> of the triple: ("Four score and seven years ago.") ==(Quotation-of)==&gt; (Abraham Lincoln)<br/>
                    <u>Airline</u> is the <u>Object</u> of the triple: (Richard Branson) ==(Owner-of)==&gt; (Airline)
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">anchor</td>
                            <td className="meaning">The <i>extractedText</i> from the entity <i>mentions</i> list that represents the object of the relationship.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">id</td>
                            <td className="meaning">The unique identifier assigned to a disambiguated entity, and can be used to find the entity or additional fields Finch Knowledgebase.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">internalRef</td>
                            <td className="meaning">An identifier used to locate this entity in  <i>entities</i> and in <i>relationship</i>.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">start</td>
                            <td className="meaning">The starting index of the <i>extractedText</i> in the document.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">stop</td>
                            <td className="meaning">The ending index of the <i>extractedText</i> in the document.</td>
                            <td className="type">Integer</td>
                        </tr>
                        <tr>
                            <td className="attribute">type</td>
                            <td className="meaning">
                                A string value identifying the disambiguated entity type. Subtypes, when available, are delimited by a forward slash, '/', character.<br/><br/>
                                Identical to <i>type</i> in <a href="#enrichv2_entity_object">Entity JSON</a>
                            </td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">label</td>
                            <td className="meaning">The preferred name for display purposes of the disambiguated entity.</td>
                            <td className="type">String</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="enrichv2_relationship_types">Relationship Types</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Type Value</th>
                            <th>Description </th>
                        </tr>
                    </thead>
                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">Acquired</td>
                            <td className="meaning">One COMPANY purchases most or all of another COMPANY's shares to gain control of that company.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Co-Investor-with</td>
                            <td className="meaning">Two COMPANIES, two PEOPLE, or a COMPANY and a PERSON, invest in the same, third party COMPANY.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Competitor-with</td>
                            <td className="meaning">Two COMPANIES are in direct competition with each other for business.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Customer-of</td>
                            <td className="meaning">One COMPANY (the customer) purchases resources (goods or services) from another COMPANY (the supplier).</td>
                        </tr>
                        <tr>
                            <td className="attribute">Director-of</td>
                            <td className="meaning">A PERSON is on the board of directors (but is not founder/owner) of the mentioned COMPANY.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Educated-at</td>
                            <td className="meaning">A PERSON is educated at – i.e. graduated from, studies at, or attends an ORGANIZATION with sub-type EDUCATIONAL.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Employer-of</td>
                            <td className="meaning">A COMPANY or ORGANIZATION employs a PERSON who is not a director, founder or owner of the COMPANY.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Founder-of</td>
                            <td className="meaning">A PERSON founded or co-founded a COMPANY.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Invested-in</td>
                            <td className="meaning">A PERSON or a COMPANY invests capital in or purchases shares of a COMPANY without gaining control of the COMPANY.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Has-Member</td>
                            <td className="meaning">An ORGANIZATION, such as a political party, sports team, committee, performing group, etc. has or is made up of members including a PERSON.</td>
                        </tr>
                        {/*
                        <tr>
                            <td className="attribute">Organization-Location</td>
                            <td className="meaning">An ORGANIZATION has a relationship with a GEO (location) entity.</td>
                        </tr>
                        */}
                        <tr>
                            <td className="attribute">Owner-of</td>
                            <td className="meaning">A PERSON is the owner or co-owner of an ORGANIZATION, or they own at least 50% shares/stake in a COMPANY.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Partner-of</td>
                            <td className="meaning">A business arrangement exists in which two or more COMPANIES agree to pool their resources for the purpose of accomplishing a specific task (includes agreements, deals, joint ventures, and collaborations).</td>
                        </tr>
                        {/*
                        <tr>
                            <td className="attribute">Person-Location</td>
                            <td className="meaning">A PERSON has a relationship with a a GEO (location) entity.</td>
                        </tr>
                        */}
                        <tr>
                            <td className="attribute">Relative-of</td>
                            <td className="meaning">A familial or romantic relationship exists between two PEOPLE.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Subsidiary-of</td>
                            <td className="meaning">One COMPANY (the parent) owns at least 50% shares/stake in or is the Ultimate Beneficial Owner (UBO) of another COMPANY (the subsidiary).</td>
                        </tr>
                        <tr>
                            <td className="attribute">Has-Ethnicity</td>
                            <td className="meaning">Entity refers to a race or Ethnicity of a person or group of people.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Ethnicity-of</td>
                            <td className="meaning">Race or Ethnicity of a person or group of people refers to an entity.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Has-Language</td>
                            <td className="meaning">Entity refers to a language.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Language-of</td>
                            <td className="meaning">Language refers to an entity.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Has-Nationality</td>
                            <td className="meaning">Entity refers to a particular region which could be country, continent, region of a person or group of people.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Nationality-of</td>
                            <td className="meaning">Particular region which could be country, continent, region of a person or group of people that refers to an entity.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Quotation-of</td>
                            <td className="meaning">Quotation from a person or organization entity.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Has-DOB</td>
                            <td className="meaning">A PERSON his/her date of birth.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Has-DOD</td>
                            <td className="meaning">A PERSON his/her date of death.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Person-Email</td>
                            <td className="meaning">A PERSON his/her email.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Person-Phone</td>
                            <td className="meaning">A PERSON his/her phone number.</td>
                        </tr>
                        <tr>
                            <td className="attribute">Person-Twitter</td>
                            <td className="meaning">A PERSON his/her Twitter tag.</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="enrichv2_tagmodel_object">Tag Model JSON Object</h4>
                <p className="description enrich">Contains tag enrichments extracted from the document produced by one of the provided <a href="#tag_models"><i>tagModels</i></a>.</p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example:<br/> 
                    In an internal document is provided detailing how changes to corporate structure will enable more information sharing with stakeholders. Additionally it details a push for more leisure time to allow employee engagement in volunteer efforts.<br/><br/>
                    Utilizing the "esg" Tag Model it may extract a tag related to Governance due to the change in corporate structure and information sharing.<br/>
                    Utilizing the "esg" Tag Model it may extract a tag related to Social responsibility due to both employee wellbeing and community impact.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">model</td>
                            <td className="meaning">The name of the model used to extract the <i>tags</i> from the document.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">tags</td>
                            <td className="meaning">Contains tag enrichments extracted from the document by the associated <i>model</i>.<br/><br/>
                            </td>
                            <td className="type"> <a href="#enrichv2_tag_object">Tag JSON Object</a> Array</td>
                        </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="enrichv2_tag_object">Tag JSON Object</h4>
                <p className="description enrich">This defines information for a tag.</p>
                <div className="success">
                    <span className="fa fa-compass fa-xl">&nbsp;</span>
                    Example:<br/> 
                    In an internal document is provided detailing how changes to corporate structure will enable more information sharing with stakeholders. Additionally it details a push for more leisure time to allow employee engagement in volunteer efforts.<br/><br/>
                    Utilizing the "esg" Tag Model it may extract a tag with:<br/> 
                    A specific "Corporate Structure Governance" label/code.<br/>
                    A description of the code.<br/>
                    A link to documentation for the "Corporate Governance" code.<br/>
                    A score produced by the model.<br/>
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                        <tr>
                            <th>Name </th>
                            <th>Description </th>
                            <th>Type </th>
                        </tr>
                    </thead>
                    <tbody className="table-body">
                        <tr>
                            <td className="attribute">code</td>
                            <td className="meaning">A unique code string identifying the classification.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">label †</td>
                            <td className="meaning">A unique label identifying the classification.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">description</td>
                            <td className="meaning">Short description of the classification.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">link</td>
                            <td className="meaning">A documentation reference link for the classification.</td>
                            <td className="type">String</td>
                        </tr>
                        <tr>
                            <td className="attribute">score †</td>
                            <td className="meaning">The score associated with the determined classification.</td>
                            <td className="type">
                                Decimal<br/><br/>
                                Bounding Range: [0.0 - 1.0]
                            </td>
                        </tr>
                    </tbody>
                </table>
                    
                    {/* 
                    breakdown:
                        Disambiguation: NICK
                        Sentiment: LISA
                        Relationships: ADAM 
                        TAGS: LISA
                        Salience: ADAM
                        Document Summary: ADAM
                        revisit overall description and examples, maybe have chart or more details link that goes to appendix.
                    */}
                {/* make this section its on page, appendix, or in topic vector page */}
                <h4 className="heading-2" id="enrichv2_document_similarity">Measuring Document Similarity with Topic Vectors</h4>
                <div className="description enrich">
                    A topic vector is a set of topics and an associated weight for each topic, which represents the content of an article 
                    of text in a mathematical way. {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/Cosine_similarity','Cosine Similarity')} can be 
                    used to generate a similarity score indicating how related two 
                    documents are by looking at the cosine of the angle between two topic vectors.
                </div>
                <div className="description enrich">Finch topic vectors are generated 
                    from a patented Multi-Component LDA (MC-LDA) topic model. The default "news" topic 
                    model contains 200 topics and five components verified to 
                    produce high accuracy in global news. A topic vector contains two arrays of equal 
                    length consisting of the inferred Finch topic IDs and their associated weights. 
                </div>

                <div className="description enrich">The equation for cosine similarity is defined as:</div>
                <img className="cosine-image size1" src={cosineDefinition}/>
                
                <div className="description enrich">The resulting similarity score ranges from −1.0 to 1.0. The closer the score is to zero, the more related the documents.</div>
                <h4 className="subheader2">Worked Example</h4>
                
                <div className="description enrich">To compute document similarity, determine the intersection of topic IDs between two document topic vectors.</div>     
                <img className="cosine-image size3" src={cosineIntersection}/>                  
                
                <div className="description enrich">Compute the dot product by multiplying the corresponding topic weights from each vector and sum.</div>                         
                <img className="cosine-image size1" src={cosineDotProduct}/> 
                <div className="description enrich">Compute the product of magnitudes for each vector where the magnitude is calculated from the square root of the summed squares of each weight.</div> 
                <img className="cosine-image size2" src={cosineProductMagnitudes}/>
                
                <div className="description enrich">Finally, divide the dot product by the product of magnitudes to determine the similarity measure.</div> 
                <img className="cosine-image size3" src={cosineSimilarity}/> 
            </div>
            
        );
    }
}

export default DocV2Enrich;